import React, { useCallback, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/Table';
import { Download, Upload, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler';

const TEACHER_HEADERS = [
  'display_name',
  'email',
  'phone',
  'first_name',
  'last_name',
  'employee_code',
  'joining_date',
  'notes',
];
const TEACHER_SAMPLE_ROW = [
  'Teacher Dhana',
  'teacher@example.com',
  '9685741230',
  'Dhana',
  'C',
  'EMP-002',
  '2026-01-14',
  '',
];

function parseCSV(text: string): string[][] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  return lines.map((line) => {
    const row: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if ((c === ',' && !inQuotes) || (c === '\t' && !inQuotes)) {
        row.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    row.push(current.trim());
    return row;
  });
}

function buildCSV(headers: string[], sampleRow: string[]): string {
  return [headers.join(','), sampleRow.join(',')].join('\n');
}

function downloadCSV(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const INSTRUCTIONS_TEACHER = [
  'Download the sample sheet below to see the required column format.',
  'Fill in your teacher data. Keep the header row unchanged.',
  'Required columns: display_name, email. Others are optional.',
  'Save the file as CSV (e.g. teachers.csv).',
  'Upload the file here and review the preview before importing.',
  'Bulk import will create all listed teachers. Backend bulk API is not yet available — this is a preview flow.',
];

export const BulkImportModal: React.FC<BulkImportModalProps> = ({ isOpen, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<string[][]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const title = 'Import Teachers';
  const instructions = INSTRUCTIONS_TEACHER;
  const headers = TEACHER_HEADERS;
  const sampleRow = TEACHER_SAMPLE_ROW;
  const sampleFilename = 'teachers_sample.csv';

  const sampleCSV = useMemo(
    () => buildCSV(headers, sampleRow),
    [headers, sampleRow]
  );

  const handleDownloadSample = useCallback(() => {
    downloadCSV(sampleFilename, sampleCSV);
    showSuccessToast('Sample sheet downloaded.');
  }, [sampleFilename, sampleCSV]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f) return;
      setFile(f);
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = String(reader.result ?? '');
          const parsed = parseCSV(text);
          if (parsed.length < 2) {
            showErrorToast('File must have a header row and at least one data row.');
            setRows([]);
            return;
          }
          setRows(parsed);
        } catch (err) {
          showErrorToast('Could not parse CSV.');
          setRows([]);
        }
      };
      reader.readAsText(f, 'UTF-8');
      e.target.value = '';
    },
    []
  );

  const headerRow = rows[0] ?? [];
  const dataRows = rows.slice(1);
  const hasData = dataRows.length > 0;

  const handleRunImport = useCallback(async () => {
    if (!hasData) {
      showErrorToast('Upload a CSV file with at least one data row first.');
      return;
    }
    setIsImporting(true);
    try {
      // Mock: backend does not have bulk import yet
      await new Promise((r) => setTimeout(r, 800));
      showSuccessToast(
        `Preview: ${dataRows.length} teacher(s) would be imported. Bulk import API is not available yet — this is a mock flow.`
      );
      onClose();
      setFile(null);
      setRows([]);
    } catch {
      showErrorToast('Import failed.');
    } finally {
      setIsImporting(false);
    }
  }, [hasData, dataRows.length, onClose]);

  const handleClose = useCallback(() => {
    setFile(null);
    setRows([]);
    onClose();
  }, [onClose]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} size="xl">
      <div className="flex flex-col gap-6 p-1 max-h-[calc(90vh-8rem)] overflow-y-auto">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Instructions</h3>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-600 dark:text-gray-400">
            {instructions.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleDownloadSample}
            className="inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download sample sheet
          </Button>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium">
            <Upload className="h-4 w-4" />
            {file ? file.name : 'Choose CSV file'}
            <input
              type="file"
              accept=".csv"
              className="sr-only"
              onChange={handleFileChange}
            />
          </label>
        </div>

        {hasData && (
          <>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <FileSpreadsheet className="h-4 w-4" />
              <span>
                {dataRows.length} row(s) ready to import. Backend bulk import is not available — preview only.
              </span>
            </div>
            <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {headerRow.map((h, i) => (
                      <TableHead key={i} className="whitespace-nowrap">
                        {h || `Column ${i + 1}`}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataRows.slice(0, 10).map((row, rIdx) => (
                    <TableRow key={rIdx}>
                      {headerRow.map((_, cIdx) => (
                        <TableCell key={cIdx} className="max-w-[200px] truncate">
                          {row[cIdx] ?? '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {dataRows.length > 10 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 px-4 py-2 border-t border-gray-200 dark:border-gray-600">
                  Showing first 10 of {dataRows.length} rows.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 p-3 text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>
                This is a mock flow. No records will be created until the backend supports bulk import.
              </span>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 pt-4">
          <Button variant="ghost" onClick={handleClose} type="button">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleRunImport}
            disabled={!hasData}
            isLoading={isImporting}
          >
            {hasData ? `Import ${dataRows.length} teacher(s) (preview)` : 'Import'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
